import React from 'react'
import { ProductImage } from 'app/common/Common'
import DownloadIcon from 'client/utils/icons/download-icon'
import PlusIcon from 'client/utils/icons/plus-icon'
import TrashIcon from 'client/utils/icons/trash-icon'
import {
  $DCDangerColor,
  $DCDangerLightColor,
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcInput,
  DcOverlaySlider,
  DcTag,
  DcText,
  PixelCurrency,
  PixelFlexBox,
  PixelPageContainer,
  PixelTag,
  PixelText,
  Section,
  SectionContent,
} from 'pixel-base'
import DcScanner from 'pixel-base/components/dc-scanner'
import styled from 'styled-components'

import { ActionButtons } from './ActionButtons'
import SearchProduct from './component/SearchProduct'
import { FileUploadDropzone } from './FileUploadDropzone'
import { ProductSearch } from './ProductSearch'
import { TruncatedTextWithTooltip } from './TruncatedTextWithTooltip'
import { UploadResultsModal } from './UploadResultsModal'
import { usePurchaseEdit } from './usePurchaseEdit'
import {
  IPurchaseItem,
  ISupplier,
  ISearchProduct,
  IWarehouse,
} from '../../../../../../../decl'
import AddSupplier from '../../../supplier/components/AddSupplier'

// Styled components for store display
const StoreChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`

const StoreChip = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
  white-space: nowrap;
`

const NoLinksMessage = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  white-space: nowrap;
`

const PurchaseEdit = ({
  purchase_id,
  warehouse_id,
  setShowDetailModal,
  setPurchaseId,
  detailType,
  setDetailType,
  setReload,
}: {
  purchase_id: string
  warehouse_id: string
  action: string
  setShowDetailModal: (_value: boolean) => void
  setPurchaseId: (_value: string) => void
  detailType: string
  setDetailType: (_value: string) => void
  reload: boolean
  setReload: (_value: boolean) => void
}) => {
  const {
    formEditAble,
    setFormEditAble,
    showScanner,
    setShowScanner,
    showFileUpload,
    setShowFileUpload,
    showUploadModal,
    setShowUploadModal,
    scannedSKU,
    setScannedSKU,
    uploadedFile,
    setUploadedFile,
    uploadProgress,
    setUploadProgress,
    uploadResults,
    setUploadResults,
    addNewSupplierModal,
    setAddNewSupplierModal,
    purchaseRef,
    fileInputRef,
    scannerRef,
    showSearchProduct,
    setShowSearchProduct,
    suppliers,
    setSuppliers,
    searchValue,
    setSearchValue,
    purchaseDetail,
    warehouseList,
    newPurchase,
    setNewPurchase,
    error,
    categories,
    stopScanner,
    handleScan,
    handleFileUploadFromModal,
    downloadTemplate,
    processUploadedFile,
    validateProductsFromAPI,
    getAllCategories,
    updatePurchase,
    updatePurchasesStatus,
    getAllSuppliers,
    handleQuantityChange,
    handlePurchasePriceChange,
    addPurchase,
    totalItems,
    discardChanges,
    totalPrice,
    currentWarehouse,
    debounceFn,
    showSuccess,
    showFailure,
    showPageLoader,
    hidePageLoader,
    showLoader,
    hideLoader,
    tenant_info,
  } = usePurchaseEdit(
    purchase_id,
    warehouse_id,
    setShowDetailModal,
    setPurchaseId,
    detailType,
    setDetailType,
    setReload,
  )

  // Determine the current mode based on detailType and formEditAble
  const isNewPurchase =
    detailType === 'new' || purchase_id === undefined || purchase_id === null
  const isViewMode = detailType === 'return'
  const isEditMode = detailType === 'edit'

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (formEditAble && purchase_id !== undefined) {
      updatePurchase()
    } else {
      addPurchase()
    }
  }

  // Wrapper function to adapt the scanner interface
  const handleScannerScan = (sku: string) => {
    // Create a mock event object that matches the expected interface
    const mockEvent = {
      target: { value: sku },
    } as React.ChangeEvent<HTMLInputElement>
    handleScan(mockEvent)
  }

  return (
    <>
      <PixelPageContainer padding="0px">
        <form onSubmit={handleSubmit}>
          <Section style={{ padding: '0px' }}>
            <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
              <PixelFlexBox
                gap={'10px'}
                justifyContent={'space-between'}
                height={'auto'}
                style={{ padding: '5px 20px ' }}
              >
                <ActionButtons
                  purchase_id={purchase_id}
                  isNewPurchase={isNewPurchase}
                  isViewMode={isViewMode}
                  isEditMode={isEditMode}
                  setShowScanner={setShowScanner}
                  setShowUploadModal={setShowUploadModal}
                  setDetailType={setDetailType}
                  setPurchaseId={setPurchaseId}
                  setFormEditAble={setFormEditAble}
                  getAllSuppliers={getAllSuppliers}
                  getAllCategories={getAllCategories}
                  discardChanges={discardChanges}
                  updatePurchasesStatus={updatePurchasesStatus}
                  currentWarehouse={currentWarehouse}
                />
              </PixelFlexBox>
            </SectionContent>
          </Section>
          <PixelFlexBox
            gap={'4px'}
            padding={'20px 12px'}
            height="calc(100vh - 102px)"
            style={{ overflow: 'auto' }}
          >
            <DcFlexBox
              style={{
                flexDirection: 'column',
                justifyContent: 'baseline',
                width: '60%',
              }}
            >
              <Section
                className="purchase"
                ref={purchaseRef}
                style={{ height: '98%' }}
                noPadding
              >
                <SectionContent style={{ minHeight: '100%' }}>
                  <DcFlexBox flexDirection="column">
                    {isNewPurchase || isEditMode ? (
                      <ProductSearch
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        debounceFn={debounceFn}
                        setShowSearchProduct={setShowSearchProduct}
                      />
                    ) : (
                      <PixelText
                        textSize="16px"
                        style={{
                          padding: '20px 20px 0px 20px',
                          fontWeight: '600',
                          color: 'rgba(0, 0, 0, 0.84)',
                        }}
                      >
                        Product List
                      </PixelText>
                    )}
                    <DcDiv height="auto">
                      {newPurchase?.purchase_items?.length > 0 && (
                        <DcFlexBox
                          style={{
                            padding: '10px 15px',
                            background: '#f8f9fa',
                            borderBottom: '2px solid #dee2e6',
                            borderRadius: '8px 8px 0 0',
                          }}
                          height="auto"
                          gap="5px"
                        >
                          <DcFlexBox
                            alignItems="center"
                            justifyContent="flex-start"
                            width="25%"
                            style={{ fontSize: '14px', fontWeight: '600' }}
                          >
                            Product
                          </DcFlexBox>
                          <DcFlexBox
                            alignItems="center"
                            justifyContent="flex-start"
                            width="15%"
                            style={{ fontSize: '14px', fontWeight: '600' }}
                          >
                            Quantity
                          </DcFlexBox>
                          {isViewMode ? (
                            <DcFlexBox
                              alignItems="center"
                              justifyContent="flex-start"
                              width="15%"
                              style={{ fontSize: '14px', fontWeight: '600' }}
                            >
                              {`Price(${tenant_info?.default_currency_symbol})`}
                            </DcFlexBox>
                          ) : (
                            <DcFlexBox
                              alignItems="center"
                              justifyContent="flex-start"
                              width="15%"
                              style={{ fontSize: '14px', fontWeight: '600' }}
                            >
                              Purchase Price
                            </DcFlexBox>
                          )}
                          <DcFlexBox
                            alignItems="center"
                            justifyContent="flex-start"
                            width="15%"
                            style={{ fontSize: '14px', fontWeight: '600' }}
                          >
                            Stores
                          </DcFlexBox>
                          <DcFlexBox
                            alignItems="center"
                            justifyContent="flex-end"
                            width={isNewPurchase || isEditMode ? '18%' : '25%'}
                            style={{ fontSize: '14px', fontWeight: '600' }}
                          >
                            {`Total(${tenant_info?.default_currency_symbol})`}
                          </DcFlexBox>
                          {(isNewPurchase || isEditMode) && (
                            <DcFlexBox
                              alignItems="center"
                              justifyContent="center"
                              width="7%"
                              style={{ fontSize: '14px', fontWeight: '600' }}
                            >
                              Action
                            </DcFlexBox>
                          )}
                        </DcFlexBox>
                      )}
                    </DcDiv>
                  </DcFlexBox>

                  {/* Warning for missing store links */}
                  {newPurchase?.purchase_items?.some(
                    (item) =>
                      !item.store_links || item.store_links.length === 0,
                  ) && (
                    <DcFlexBox
                      style={{
                        margin: '0px 10px 10px 10px',
                        padding: '12px 16px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeaa7',
                        borderRadius: '8px',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <DcText
                        style={{
                          fontSize: '14px',
                          color: '#856404',
                          fontWeight: '500',
                        }}
                      >
                        ⚠️ Warning: Products without store links will not update
                        inventory on live stores.
                      </DcText>
                    </DcFlexBox>
                  )}

                  {/* Product List */}
                  <DcDiv
                    height="calc(100vh - 351px)"
                    style={{
                      overflow: 'auto',
                      margin: '0px 10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {newPurchase?.purchase_items?.length > 0 && (
                      <>
                        {newPurchase.purchase_items.map(
                          (item: IPurchaseItem, i: number) => {
                            return (
                              <DcFlexBox
                                height="auto"
                                style={{
                                  flexDirection: 'column',
                                  justifyContent: 'space-around',
                                  alignItems: 'center',
                                  alignContent: 'center',
                                  height: '70px',
                                  margin: '0px !important',
                                  background: '#ffffff',
                                  border: '1px solid #e9ecef',
                                  borderRadius: '8px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                  transition: 'all 0.2s ease',
                                }}
                                key={i}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow =
                                    '0 4px 8px rgba(0,0,0,0.1)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow =
                                    '0 2px 4px rgba(0,0,0,0.05)'
                                }}
                              >
                                <DcFlexBox
                                  height="auto"
                                  gap="5px"
                                  padding="0px"
                                  style={{ width: '100%', minWidth: 0 }}
                                >
                                  <DcFlexBox
                                    alignItems="center"
                                    justifyContent="flex-start"
                                    width="25%"
                                    style={{ minWidth: 0, overflow: 'hidden' }}
                                  >
                                    <ProductImage
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        marginRight: '10px',
                                        borderRadius: '4px',
                                        objectFit: 'cover',
                                        flexShrink: 0,
                                      }}
                                      path={item.product?.product_image_path}
                                    />
                                    <DcFlexBox
                                      flexDirection="column"
                                      style={{
                                        minWidth: 0,
                                        flex: 1,
                                        overflow: 'hidden',
                                      }}
                                    >
                                      <TruncatedTextWithTooltip
                                        text={item.product?.product_sku}
                                        maxLength={35}
                                        style={{
                                          fontSize: '12px',
                                          fontWeight: 600,
                                        }}
                                      />
                                      <TruncatedTextWithTooltip
                                        text={item.product?.product_name}
                                        maxLength={60}
                                        style={{
                                          fontSize: '10px',
                                          color: '#6c757d',
                                        }}
                                      />
                                    </DcFlexBox>
                                  </DcFlexBox>
                                  <DcFlexBox
                                    alignItems="center"
                                    justifyContent="flex-start"
                                    width="15%"
                                  >
                                    {isViewMode ? (
                                      <DcText
                                        textSize="14px"
                                        style={{ fontWeight: '500' }}
                                      >
                                        {item.received_count}
                                      </DcText>
                                    ) : (
                                      <DcInput
                                        inputSize="small"
                                        isShowLabel={false}
                                        height="35px"
                                        required
                                        noPadding
                                        min="0"
                                        type={'number'}
                                        name={'Quantity'}
                                        placeholder={'0'}
                                        value={item.received_count}
                                        onChange={(e) =>
                                          handleQuantityChange(e, i)
                                        }
                                        style={{
                                          border: '1px solid #ced4da',
                                          borderRadius: '4px',
                                          padding: '0 8px',
                                        }}
                                      />
                                    )}
                                  </DcFlexBox>
                                  <DcFlexBox
                                    alignItems="center"
                                    justifyContent="flex-start"
                                    width="15%"
                                  >
                                    {isViewMode ? (
                                      <DcText
                                        textSize="14px"
                                        style={{ fontWeight: '500' }}
                                      >
                                        {item.purchase_price}
                                      </DcText>
                                    ) : (
                                      <DcInput
                                        inputSize="small"
                                        isShowLabel={false}
                                        height="35px"
                                        noPadding
                                        min="0"
                                        type={'number'}
                                        name={'Purchase Price'}
                                        placeholder={'0'}
                                        value={item.purchase_price}
                                        onChange={(e) =>
                                          handlePurchasePriceChange(e, i)
                                        }
                                        style={{
                                          border: '1px solid #ced4da',
                                          borderRadius: '4px',
                                          padding: '0 8px',
                                        }}
                                      />
                                    )}
                                  </DcFlexBox>
                                  <DcFlexBox
                                    alignItems="center"
                                    justifyContent="flex-start"
                                    width="15%"
                                  >
                                    <StoreChipsContainer>
                                      {item.store_links &&
                                      item.store_links.length > 0 ? (
                                        item.store_links.map((store, index) => (
                                          <StoreChip
                                            key={`${
                                              store.store_id || index
                                            }-${index}`}
                                          >
                                            {store.store_name}
                                          </StoreChip>
                                        ))
                                      ) : (
                                        <NoLinksMessage>
                                          Link Missing
                                        </NoLinksMessage>
                                      )}
                                    </StoreChipsContainer>
                                  </DcFlexBox>

                                  <DcFlexBox
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    width="18%"
                                  >
                                    <DcText
                                      textSize="14px"
                                      style={{
                                        fontWeight: '600',
                                        color: '#28a745',
                                      }}
                                    >
                                      {totalPrice(item)}
                                    </DcText>
                                  </DcFlexBox>
                                  {(isNewPurchase || isEditMode) && (
                                    <DcFlexBox
                                      alignItems="center"
                                      justifyContent="center"
                                      width="7%"
                                    >
                                      <DcButton
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => {
                                          setNewPurchase({
                                            ...newPurchase,
                                            purchase_items:
                                              newPurchase.purchase_items?.filter(
                                                (index) =>
                                                  index.product.product_id !==
                                                  item.product.product_id,
                                              ),
                                          })
                                        }}
                                        style={{ padding: '4px 8px' }}
                                      >
                                        <TrashIcon size={16} />
                                      </DcButton>
                                    </DcFlexBox>
                                  )}
                                </DcFlexBox>
                              </DcFlexBox>
                            )
                          },
                        )}
                      </>
                    )}
                  </DcDiv>

                  {/* Summary Section */}
                  {newPurchase?.purchase_items?.length > 0 && (
                    <DcFlexBox
                      style={{
                        justifyContent: 'space-between',
                        alignContent: 'center',
                        alignItems: 'center',
                        borderTop: '2px solid #dee2e6',
                        width: '98.3%',
                        paddingTop: '15px',
                        paddingBottom: '10px',
                        background: '#f8f9fa',
                        borderRadius: '0 0 8px 8px',
                        marginTop: '10px',
                      }}
                    >
                      <DcDiv
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#495057',
                        }}
                      >
                        Selected Products ({newPurchase.purchase_items?.length})
                      </DcDiv>
                      <DcDiv
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#495057',
                        }}
                      >
                        Total Items: {totalItems()}
                      </DcDiv>
                      <DcDiv
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#28a745',
                        }}
                      >
                        Total Cost:
                        <PixelCurrency
                          currency={'PKR'}
                          value={newPurchase?.total_amount}
                        />
                      </DcDiv>
                    </DcFlexBox>
                  )}
                </SectionContent>
              </Section>
            </DcFlexBox>

            {/* Right Panel - Purchase Information */}
            <DcFlexBox
              style={{
                flexDirection: 'column',
                flexWrap: 'nowrap',
                justifyContent: 'baseline',
                alignItems: 'center',
                alignContent: 'center',
                gap: '20px',
                width: '40%',
              }}
            >
              <Section noPadding className="purchase" ref={purchaseRef}>
                {isNewPurchase || isEditMode ? (
                  <SectionContent
                    style={{
                      gap: '15px',
                      display: 'flex',
                      flexDirection: 'column',
                      background: '#ffffff',
                      padding: '20px',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    }}
                  >
                    {purchase_id && (
                      <DcFlexBox
                        height="auto"
                        style={{
                          borderBottom: '1px solid #e6e6e6',
                          width: '100%',
                          paddingBottom: '10px',
                          marginBottom: '10px',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#495057',
                          }}
                        >
                          Status:
                        </span>
                        <PixelTag style={{ marginLeft: '10px' }}>
                          {purchaseDetail?.purchase_status?.status}
                        </PixelTag>
                      </DcFlexBox>
                    )}

                    {/* Supplier Selection */}
                    <DcFlexBox height="auto" flexDirection="column" gap="8px">
                      <DcFlexBox
                        height="auto"
                        style={{ alignItems: 'flex-end', gap: '8px' }}
                      >
                        <DcFlexBox
                          height="auto"
                          flexDirection="column"
                          style={{ flex: 1 }}
                        >
                          <DcText
                            fontSize=".875rem"
                            style={{
                              fontWeight: '600',
                              color: '#495057',
                              marginBottom: '5px',
                            }}
                          >
                            Supplier *
                          </DcText>
                          <DcDropDown
                            error={
                              error.field === 'supplier_id' &&
                              error.error !== ''
                                ? error.error
                                : undefined
                            }
                            customLabel="Supplier"
                            required
                            placeholder="Select Supplier"
                            options={[
                              {
                                supplier_name: 'Select Supplier',
                                id: '',
                              },
                              ...suppliers,
                            ].map((data: ISupplier) => {
                              return {
                                label: data.supplier_name,
                                value: data.id,
                              }
                            })}
                            value={newPurchase?.supplier?.id || ''}
                            onChange={(e) => {
                              const selectedSupplier = suppliers.find(
                                (s) => s.id === e.target.value,
                              )
                              setNewPurchase({
                                ...newPurchase,
                                supplier: selectedSupplier || {
                                  id: 0,
                                  supplier_name: '',
                                  contact_person: '',
                                  phone: '',
                                },
                              })
                            }}
                          />
                        </DcFlexBox>
                        <DcButton
                          onClick={() => {
                            setAddNewSupplierModal(true)
                          }}
                          variant={'outline-primary'}
                        >
                          <PlusIcon size={16} />
                        </DcButton>
                      </DcFlexBox>
                    </DcFlexBox>

                    {/* Warehouse Selection */}
                    <DcFlexBox height="auto" flexDirection="column" gap="8px">
                      <DcText
                        fontSize=".875rem"
                        style={{
                          fontWeight: '600',
                          color: '#495057',
                          marginBottom: '5px',
                        }}
                      >
                        Warehouse *
                      </DcText>
                      <DcDropDown
                        error={
                          error.field === 'warehouse_id' && error.error !== ''
                            ? error.error
                            : undefined
                        }
                        customLabel="Warehouse"
                        required
                        placeholder={'Select Warehouse'}
                        options={[
                          { name: 'Select Warehouse', wh_id: 0 },
                          ...warehouseList,
                        ].map((data: IWarehouse) => {
                          return {
                            label: data.name,
                            value: data.wh_id,
                          }
                        })}
                        value={newPurchase?.warehouse?.id || ''}
                        onChange={(e) => {
                          const selectedWarehouse = warehouseList.find(
                            (w) => w.wh_id === e.target.value,
                          )
                          setNewPurchase({
                            ...newPurchase,
                            warehouse: selectedWarehouse
                              ? {
                                  id: selectedWarehouse.wh_id,
                                  name: selectedWarehouse.name,
                                }
                              : {
                                  id: 0,
                                  name: '',
                                },
                          })
                        }}
                      />
                    </DcFlexBox>

                    {/* Purchase Order Number */}
                    <DcFlexBox height="auto" flexDirection="column" gap="8px">
                      <DcText
                        fontSize=".875rem"
                        style={{
                          fontWeight: '600',
                          color: '#495057',
                          marginBottom: '5px',
                        }}
                      >
                        Purchase Order Number *
                      </DcText>
                      <DcInput
                        name="po_number"
                        error={
                          error.field === 'po_number' && error.error !== ''
                            ? error.error
                            : undefined
                        }
                        required
                        type={'text'}
                        placeholder={'Enter PO Number'}
                        value={newPurchase.reference_number}
                        onChange={(e) =>
                          setNewPurchase({
                            ...newPurchase,
                            reference_number: e.target.value,
                          })
                        }
                      />
                    </DcFlexBox>

                    {/* Notes */}
                    <DcFlexBox height="auto" flexDirection="column" gap="8px">
                      <DcText
                        fontSize=".875rem"
                        style={{
                          fontWeight: '600',
                          color: '#495057',
                          marginBottom: '5px',
                        }}
                      >
                        Notes
                      </DcText>
                      <DcInput
                        name="notes"
                        as="textarea"
                        error={
                          error.field === 'notes' && error.error !== ''
                            ? error.error
                            : undefined
                        }
                        type={'text'}
                        placeholder={'Add notes about this purchase...'}
                        value={newPurchase.notes}
                        onChange={(e) =>
                          setNewPurchase({
                            ...newPurchase,
                            notes: e.target.value,
                          })
                        }
                        style={{ minHeight: '80px' }}
                      />
                    </DcFlexBox>
                  </SectionContent>
                ) : (
                  <SectionContent noPadding>
                    <DcFlexBox
                      height="auto"
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px',
                      }}
                    >
                      <PixelText
                        textSize="16px"
                        style={{
                          padding: '20px 20px 0px 20px',
                          fontWeight: '600',
                          color: 'rgba(0, 0, 0, 0.84)',
                        }}
                      >
                        Purchase Information
                      </PixelText>
                    </DcFlexBox>
                    <SectionContent
                      style={{
                        background: '#f8f9fa',
                        padding: '20px',
                        borderRadius: '8px',
                      }}
                    >
                      <DcFlexBox height="auto" style={{ marginBottom: '10px' }}>
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            width: '50%',
                            color: '#6c757d',
                          }}
                        >
                          Status:
                        </span>
                        <DcTag
                          customBackgroundColor={
                            purchaseDetail?.purchase_status?.status === 'Draft'
                              ? $DCDangerLightColor
                              : undefined
                          }
                          textColor={
                            purchaseDetail?.purchase_status?.status === 'Draft'
                              ? $DCDangerColor
                              : undefined
                          }
                        >
                          {purchaseDetail?.purchase_status?.status}
                        </DcTag>
                      </DcFlexBox>
                      <DcFlexBox height="auto" style={{ marginBottom: '10px' }}>
                        <span
                          style={{
                            color: '#6c757d',
                            fontSize: '14px',
                            fontWeight: '600',
                            width: '50%',
                          }}
                        >
                          Supplier:
                        </span>
                        <PixelText
                          textSize="14px"
                          style={{ fontWeight: '500' }}
                        >
                          {purchaseDetail?.supplier?.supplier_name || '--'}
                        </PixelText>
                      </DcFlexBox>
                      <DcFlexBox height="auto" style={{ marginBottom: '10px' }}>
                        <span
                          style={{
                            color: '#6c757d',
                            fontSize: '14px',
                            fontWeight: '600',
                            width: '50%',
                          }}
                        >
                          Deliver to Warehouse:
                        </span>
                        <PixelText
                          textSize="14px"
                          style={{ fontWeight: '500' }}
                        >
                          {purchaseDetail?.warehouse?.name || '--'}
                        </PixelText>
                      </DcFlexBox>
                      <DcFlexBox height="auto" style={{ marginBottom: '10px' }}>
                        <span
                          style={{
                            color: '#6c757d',
                            fontSize: '14px',
                            fontWeight: '600',
                            width: '50%',
                          }}
                        >
                          Purchase Order Number:
                        </span>
                        <PixelText
                          textSize="14px"
                          style={{ fontWeight: '500' }}
                        >
                          {purchaseDetail?.reference_number || '--'}
                        </PixelText>
                      </DcFlexBox>
                      <DcFlexBox height="auto">
                        <span
                          style={{
                            color: '#6c757d',
                            fontSize: '14px',
                            fontWeight: '600',
                            width: '50%',
                          }}
                        >
                          Notes:
                        </span>
                        <PixelText
                          textSize="14px"
                          style={{ fontWeight: '500' }}
                        >
                          {purchaseDetail?.notes || '--'}
                        </PixelText>
                      </DcFlexBox>
                    </SectionContent>
                  </SectionContent>
                )}
              </Section>
            </DcFlexBox>
          </PixelFlexBox>
        </form>
      </PixelPageContainer>

      {/* Scanner Modal */}
      <DcScanner
        isOpen={showScanner}
        onClose={() => {
          setShowScanner(false)
          stopScanner()
        }}
        scannedSKU={scannedSKU}
        setScannedSKU={setScannedSKU}
        handleScan={handleScannerScan}
        scannerRef={scannerRef}
      />

      {/* File Upload Modal */}
      <DcOverlaySlider
        title={'Upload Products from File'}
        width="50%"
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      >
        <DcFlexBox flexDirection="column" gap="20px">
          <DcText textSize="14px" style={{ color: '#6c757d' }}>
            Upload a CSV or Excel file with the following columns: SKU,
            Quantity, Price
          </DcText>

          {/* Template Download Section */}
          <DcFlexBox
            style={{
              background: '#e3f2fd',
              padding: '15px',
              borderRadius: '8px',
              border: '1px solid #bbdefb',
              height: 'auto',
            }}
          >
            <DcFlexBox flexDirection="column" gap="8px" style={{ flex: 1 }}>
              <DcText
                textSize="14px"
                style={{ fontWeight: '600', color: '#1976d2' }}
              >
                📋 Download Template
              </DcText>
              <DcText textSize="12px" style={{ color: '#1976d2' }}>
                Use our template to ensure your file has the correct format
              </DcText>
            </DcFlexBox>
            <DcButton
              size="sm"
              variant="outline-primary"
              onClick={downloadTemplate}
              withIcon={true}
            >
              <DownloadIcon size={16} />
              Download Template
            </DcButton>
          </DcFlexBox>

          {/* Drag and Drop Area */}
          <DcFlexBox flexDirection="column" gap="10px">
            <DcText
              textSize="14px"
              style={{ fontWeight: '600', color: '#495057' }}
            >
              Upload File
            </DcText>
            <FileUploadDropzone onFileSelect={handleFileUploadFromModal} />
          </DcFlexBox>

          {/* File Format Info */}
          <DcFlexBox
            style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '6px',
              border: '1px solid #dee2e6',
              height: 'auto',
            }}
          >
            <DcFlexBox flexDirection="column" gap="5px">
              <DcText
                textSize="12px"
                style={{ fontWeight: '600', color: '#495057' }}
              >
                📄 Supported File Formats:
              </DcText>
              <DcText textSize="12px" style={{ color: '#6c757d' }}>
                • CSV files (.csv)
              </DcText>
              <DcText textSize="12px" style={{ color: '#6c757d' }}>
                • Excel files (.xlsx, .xls)
              </DcText>
            </DcFlexBox>
          </DcFlexBox>
        </DcFlexBox>
      </DcOverlaySlider>

      {/* Upload Results Modal */}
      <UploadResultsModal
        isOpen={showFileUpload}
        onClose={() => {
          setShowFileUpload(false)
          setUploadResults(null)
          setUploadProgress(0)
          setUploadedFile(null)
          // Reset file input value
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }}
        uploadedFile={uploadedFile}
        uploadProgress={uploadProgress}
        uploadResults={uploadResults}
        setUploadResults={setUploadResults}
        setUploadProgress={setUploadProgress}
        setUploadedFile={setUploadedFile}
        processUploadedFile={processUploadedFile}
        validateProductsFromAPI={validateProductsFromAPI}
        showSuccess={showSuccess}
        showFailure={showFailure}
        showPageLoader={showPageLoader}
        hidePageLoader={hidePageLoader}
        showLoader={showLoader}
        hideLoader={hideLoader}
      />

      {/* Existing Modals */}
      <DcOverlaySlider
        title={'Add New Supplier'}
        width="50%"
        isOpen={addNewSupplierModal}
        onClose={() => setAddNewSupplierModal(false)}
      >
        {addNewSupplierModal && (
          <AddSupplier
            handleClose={(res: ISupplier) => {
              if (res) {
                setNewPurchase({
                  ...newPurchase,
                  supplier: res,
                })
                setSuppliers([res, ...suppliers])
                setAddNewSupplierModal(false)
              } else {
                setAddNewSupplierModal(false)
              }
            }}
          />
        )}
      </DcOverlaySlider>
      {showSearchProduct && (
        <DcOverlaySlider
          width="80%"
          title={'Search Product'}
          isOpen={showSearchProduct}
          onClose={() => setShowSearchProduct(false)}
        >
          {showSearchProduct && (
            <SearchProduct
              cart={newPurchase?.purchase_items}
              categories={categories}
              handleClose={(data) => {
                setShowSearchProduct(false)
                if (data) {
                  setNewPurchase({
                    ...newPurchase,
                    purchase_items: [
                      ...data.map((item: ISearchProduct) => {
                        return {
                          id: null,
                          product: {
                            product_id: item.product_id,
                            product_image_path: item.image,
                            product_name: item.product_name,
                            product_sku: item.sku,
                          },
                          accepted_count: null,
                          damaged_count: null,
                          received_count: 0,
                          product_image_path: item.image,
                          purchase_price: (item as any).cost_unit_price || 0,
                          created_at: {
                            date:
                              (item as any).created_at ||
                              new Date().toISOString(),
                            timezone_type: 0,
                            timezone: '',
                          },
                          store_links: (item as any).store_links || [],
                        }
                      }),
                      ...newPurchase.purchase_items,
                    ],
                  })
                }
              }}
              searchValue={searchValue}
            />
          )}
        </DcOverlaySlider>
      )}
    </>
  )
}

export default PurchaseEdit
